export type ImportExcelDataOutcome =
  | { status: "success"; totalRows?: number; completedRows?: number }
  | {
      status: "partial";
      message: string;
      totalRows?: number;
      completedRows?: number;
    }
  | {
      status: "failed";
      message: string;
      totalRows?: number;
      completedRows?: number;
    };

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Walk nested `{ message: "{...json...}" }` payloads from OnBoarding / Visual One. */
const unwrapImportPayload = (
  value: unknown,
  depth = 0,
): Record<string, unknown> | null => {
  const record = asRecord(value);
  if (!record) return null;
  if (depth > 4) return record;

  const message = record.message;
  if (typeof message === "string" && message.trim().startsWith("{")) {
    try {
      return unwrapImportPayload(JSON.parse(message), depth + 1) ?? record;
    } catch {
      return record;
    }
  }
  if (asRecord(message)) {
    return unwrapImportPayload(message, depth + 1) ?? record;
  }
  return record;
};

const collectMessages = (payload: Record<string, unknown>): string[] => {
  const parts: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === "string" && value.trim()) parts.push(value.trim());
  };

  push(payload.message);
  push(payload.error);
  push(payload.errorMessage);
  push(payload.detail);
  push(payload.statusMessage);

  const warnings = payload.warnings;
  if (Array.isArray(warnings)) {
    for (const warning of warnings) {
      if (typeof warning === "string") push(warning);
      else {
        const warningRecord = asRecord(warning);
        if (warningRecord) {
          push(warningRecord.message);
          push(warningRecord.error);
          push(warningRecord.description);
        }
      }
    }
  }

  const errors = payload.errors;
  if (Array.isArray(errors)) {
    for (const error of errors) {
      if (typeof error === "string") push(error);
      else {
        const errorRecord = asRecord(error);
        if (errorRecord) {
          push(errorRecord.message);
          push(errorRecord.error);
        }
      }
    }
  }

  return parts;
};

const textLooksPartial = (text: string): boolean =>
  /partial/i.test(text) ||
  /duplicate/i.test(text) ||
  /already\s+exist/i.test(text) ||
  /DUPLICATE/i.test(text);

const readNonNegativeNumber = (
  ...sources: Array<Record<string, unknown> | null>
): { totalRows?: number; completedRows?: number } => {
  const read = (key: string): number | undefined => {
    for (const source of sources) {
      if (!source) continue;
      const value = source[key];
      if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
        return value;
      }
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed >= 0) return parsed;
      }
    }
    return undefined;
  };

  return {
    totalRows: read("totalRows"),
    completedRows:
      read("completedRows") ??
      read("completedRowCount") ??
      read("rowsCompleted"),
  };
};

const formatRowProgress = (
  totalRows?: number,
  completedRows?: number,
): string | undefined => {
  if (totalRows === undefined && completedRows === undefined) return undefined;
  if (totalRows !== undefined && completedRows !== undefined) {
    return `Completed ${completedRows} of ${totalRows} rows`;
  }
  if (completedRows !== undefined) return `Completed rows: ${completedRows}`;
  return `Total rows: ${totalRows}`;
};

const withRowProgress = (
  message: string,
  totalRows?: number,
  completedRows?: number,
): string => {
  const progress = formatRowProgress(totalRows, completedRows);
  if (!progress) return message;
  if (!message.trim()) return progress;
  if (message.includes(progress)) return message;
  return `${message} (${progress})`;
};

/**
 * Interprets ImportExcelData HTTP + JSON body.
 * Visual One often returns HTTP 200 with `{ result:false, successStatus:false }`
 * (sometimes nested inside `message` as a JSON string) for duplicates / partial imports.
 */
export const interpretImportExcelDataResponse = async (
  response: Response,
): Promise<ImportExcelDataOutcome> => {
  const rawText = await response.text();
  let parsed: unknown = null;
  if (rawText.trim()) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = null;
    }
  }

  const root = asRecord(parsed);
  const payload = unwrapImportPayload(parsed) ?? root;
  const messages = payload ? collectMessages(payload) : [];
  if (!messages.length && rawText.trim() && !parsed) {
    messages.push(rawText.trim());
  }

  const { totalRows, completedRows } = readNonNegativeNumber(root, payload);
  const combinedMessage = messages.join(" | ");
  const resultFlag = payload?.result;
  const successStatus = payload?.successStatus;
  const hasFailureFlag =
    resultFlag === false ||
    successStatus === false ||
    resultFlag === "false" ||
    successStatus === "false";
  const hasPartialSignal =
    textLooksPartial(combinedMessage) ||
    textLooksPartial(rawText) ||
    (Array.isArray(payload?.warnings) && (payload?.warnings.length ?? 0) > 0);

  if (!response.ok) {
    return {
      status: hasPartialSignal ? "partial" : "failed",
      message: withRowProgress(
        combinedMessage || `ImportExcelData failed (${response.status})`,
        totalRows,
        completedRows,
      ),
      totalRows,
      completedRows,
    };
  }

  if (hasFailureFlag || hasPartialSignal) {
    return {
      status: "partial",
      message: withRowProgress(
        combinedMessage || "Import completed with errors (partial import).",
        totalRows,
        completedRows,
      ),
      totalRows,
      completedRows,
    };
  }

  return { status: "success", totalRows, completedRows };
};
